/**
 * `scan.rotation.summary` — one aggregate row replacing ~200 per-frame writes.
 *
 * Measured on a real dev scan (iOS 26.6, 2026-08-22): a single journey wrote 338
 * `pwa_logs` rows, of which 200 were three per-tick lines — `Processing rotation
 * event` (67), `[ScanUploader] preUpload() check` (67), `[ScanUploader] Received
 * frame` (66). 59% of a scan's Firestore writes, and between them they answered
 * less than this one row does. They are now `console.debug`.
 *
 * Fed from `RotationManager`'s own event stream rather than from `ScanUploader`.
 * That is deliberate: `ScanUploader` is the most subtle file in the repo — five
 * `HERE_BE_DRAGONS.md` entries — and reading the rotation FSM's public events
 * gets the same numbers without touching it.
 *
 * ── What tick pacing tells you ───────────────────────────────────────────
 *
 * `CountdownManager` drives ticks on a fixed 250ms cadence, so the intervals
 * between PROGRESS events are a direct read on whether the main thread kept up.
 * A phone that thermally throttles, or a tab the OS is starving in the
 * background, shows it as `tickIntervalP90Ms` far above the nominal — which is a
 * completely different fix from "the user span too fast", and today nothing
 * distinguishes them.
 */

import { RotationManager, RotationEvents } from "@/services/RotationManager";
import { logEvent } from "@/Utils/logger";
import { Events, Outcomes, Phases, type Outcome } from "./events";
import { onPhaseTransition } from "./phases";
import { serverPoseService } from "@/services/detection/serverPoseService";
import { rotationVideoFreeze } from "@/services/detection/videoFreeze";

interface Acc {
  startedAt: number;
  lastTickAt: number | null;
  ticks: number;
  maxTicksCount: number;
  intervals: number[];
  states: Record<string, number>;
  errorCode: string | null;
  reachedSuccess: boolean;
}

let acc: Acc | null = null;
let unsubscribers: Array<() => void> = [];

function stopListeners(): void {
  for (const off of unsubscribers) {
    try {
      off();
    } catch {
      /* ignore */
    }
  }
  unsubscribers = [];
}

function start(): void {
  stopListeners();
  acc = {
    startedAt: performance.now(),
    lastTickAt: null,
    ticks: 0,
    maxTicksCount: 0,
    intervals: [],
    states: {},
    errorCode: null,
    reachedSuccess: false,
  };

  const rm = RotationManager.getInstance();

  unsubscribers.push(
    rm.on(RotationEvents.PROGRESS, (e: any) => {
      const a = acc;
      if (!a) return;
      const t = performance.now();
      if (a.lastTickAt !== null) a.intervals.push(t - a.lastTickAt);
      a.lastTickAt = t;
      a.ticks++;
      const tc = e && typeof e.ticksCount === "number" ? e.ticksCount : 0;
      if (tc > a.maxTicksCount) a.maxTicksCount = tc;
      // The FSM's quadrant state (Front / Rotated / Back / …). The histogram
      // shows WHERE a failed rotation stalled, which the terminal error code
      // alone does not — "under-rotation" is the same code whether the user
      // stopped at 90° or at 270°.
      const st = e && typeof e.state === "string" ? e.state : "unknown";
      a.states[st] = (a.states[st] || 0) + 1;
    })
  );

  unsubscribers.push(
    rm.on(RotationEvents.ERROR, (err: any) => {
      const a = acc;
      if (!a) return;
      a.errorCode = (err && err.code) || "unknown";
    })
  );

  unsubscribers.push(
    rm.on(RotationEvents.SUCCESS, () => {
      const a = acc;
      if (a) a.reachedSuccess = true;
    })
  );
}

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const i = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return Math.round(sorted[i]);
}

function finish(outcome: Outcome, attempt: number | null): void {
  const a = acc;
  acc = null;
  stopListeners();
  if (!a) return;

  const durationMs = Math.max(0, Math.round(performance.now() - a.startedAt));
  const sorted = a.intervals.slice().sort((x, y) => x - y);

  logEvent(Events.SCAN_ROTATION_SUMMARY, "[telemetry] rotation summary", {
    phase: Phases.ROTATION,
    outcome,
    durationMs,
    data: {
      attempt,
      ticks: a.ticks,
      // 44 ticks is a complete rotation. Anything less with outcome=error says
      // how far the user got before the FSM gave up.
      maxTicksCount: a.maxTicksCount,
      stateHistogram: a.states,
      errorCode: a.errorCode,
      reachedSuccess: a.reachedSuccess,

      // ── Server-side pose ────────────────────────────────────────────────
      // The same fields the alignment row carries. THIS is the row that says
      // whether the scan actually happened, so it is the row that most needs to
      // name its own pose source: inferring it by matching journeyId and clock
      // order across two events is not sound in general, because both attempts
      // of a journey can share one journeyId — on the 2026-09-09 Android run
      // they did.
      //
      // Imported directly here, unlike in `alignmentAccumulator`, because
      // nothing in `serverPoseService` reads this module — so there is no cycle
      // to close.
      ...serverPoseService.getStats(),
      tickIntervalMedianMs: percentile(sorted, 50),
      tickIntervalP90Ms: percentile(sorted, 90),
      tickIntervalMaxMs: sorted.length ? Math.round(sorted[sorted.length - 1]) : 0,
      // The longest run of identical spin frames. >= 2000 ms is a frozen
      // camera, not a user who did not turn (videoFreeze.ts).
      videoFrozenMs: rotationVideoFreeze.frozenMs(),
    },
  });
}

let bound = false;

/** Attach to the phase model. Idempotent; call once at bootstrap. */
export function bindRotationAccumulator(): void {
  if (bound) return;
  bound = true;
  onPhaseTransition((kind, phase, outcome, _durationMs, attempt) => {
    if (phase !== Phases.ROTATION) return;
    if (kind === "enter") start();
    else finish(outcome || Outcomes.ABANDONED, attempt);
  });
}

/** Test-only. */
export function __resetRotationAccumulatorForTest(): void {
  acc = null;
  stopListeners();
  bound = false;
}
